import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip
} from 'recharts';
import { 
  Compass, TrendingUp, TrendingDown, Minus, ChevronRight, 
  Zap, Heart, Target, Users, AlertCircle, CheckCircle2,
  Moon, Sun, Activity, Brain, Loader2, Eye, EyeOff, AlertTriangle
} from 'lucide-react';
import api from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import BottomNav from '../components/BottomNav';

// Resistance pattern icons and colors
const RESISTANCE_PATTERNS = {
  procrastination: { icon: Clock, color: '#F59E0B', label: { en: 'Procrastination', tr: 'Erteleme' } },
  emotional_avoidance: { icon: EyeOff, color: '#8B5CF6', label: { en: 'Emotional Avoidance', tr: 'Duygusal Kaçınma' } },
  overexertion: { icon: Zap, color: '#EF4444', label: { en: 'Overexertion', tr: 'Aşırı Çalışma' } },
  doom_scrolling: { icon: Smartphone, color: '#6B7280', label: { en: 'Doom Scrolling', tr: 'Dijital Kaçış' } },
  fantasy_without_execution: { icon: Cloud, color: '#3B82F6', label: { en: 'Fantasy Without Action', tr: 'Eylemsiz Hayal' } },
  relational_withdrawal: { icon: UserMinus, color: '#EC4899', label: { en: 'Relational Withdrawal', tr: 'İlişkisel Geri Çekilme' } },
  perfectionism: { icon: Target, color: '#14B8A6', label: { en: 'Perfectionism', tr: 'Mükemmeliyetçilik' } },
  scattered_attention: { icon: Shuffle, color: '#F97316', label: { en: 'Scattered Attention', tr: 'Dağınık Dikkat' } },
  control_obsession: { icon: Lock, color: '#64748B', label: { en: 'Control Obsession', tr: 'Kontrol Takıntısı' } },
  pleasure_seeking: { icon: Sparkles, color: '#A855F7', label: { en: 'Pleasure Seeking', tr: 'Haz Arayışı' } },
};

// Import additional icons needed
import { Clock, Smartphone, Cloud, UserMinus, Shuffle, Lock, Sparkles } from 'lucide-react';

// Domain mapping for the 12 subdomains grouped into 4 main domains
const DOMAIN_GROUPS = {
  vitality: {
    name: { en: 'Vitality', tr: 'Canlılık' },
    icon: Zap,
    color: '#22C55E',
    subdomains: ['physical_health', 'strength_fitness', 'energy']
  },
  innerStability: {
    name: { en: 'Inner Stability', tr: 'İç Denge' },
    icon: Heart,
    color: '#8B5CF6',
    subdomains: ['emotional_stability', 'discipline_consistency', 'environment_order']
  },
  direction: {
    name: { en: 'Direction', tr: 'Yön' },
    icon: Target,
    color: '#F59E0B',
    subdomains: ['work_mission', 'money_financial', 'meaning_purpose']
  },
  belonging: {
    name: { en: 'Belonging', tr: 'Aidiyet' },
    icon: Users,
    color: '#3B82F6',
    subdomains: ['love_relationship', 'friends_community', 'learning_growth']
  }
};

const DOMAIN_LABELS = {
  en: {
    physical_health: 'Physical Health',
    strength_fitness: 'Strength',
    energy: 'Energy',
    environment_order: 'Space/Order',
    work_mission: 'Work/Mission',
    money_financial: 'Finances',
    love_relationship: 'Love',
    friends_community: 'Community',
    emotional_stability: 'Emotional',
    discipline_consistency: 'Discipline',
    learning_growth: 'Growth',
    meaning_purpose: 'Purpose',
  },
  tr: {
    physical_health: 'Fiziksel',
    strength_fitness: 'Güç',
    energy: 'Enerji',
    environment_order: 'Düzen',
    work_mission: 'İş/Misyon',
    money_financial: 'Finans',
    love_relationship: 'Aşk',
    friends_community: 'Topluluk',
    emotional_stability: 'Duygusal',
    discipline_consistency: 'Disiplin',
    learning_growth: 'Gelişim',
    meaning_purpose: 'Amaç',
  }
};

const VIEW_OPTIONS = [
  { id: 'current', label: { en: 'Current', tr: 'Güncel' } },
  { id: '30day', label: { en: '30-Day', tr: '30 Gün' } },
  { id: 'previous', label: { en: 'Previous', tr: 'Önceki' } },
];

// Solar Alignment interpretation
const getSolarInterpretation = (score, language) => {
  const interpretations = {
    en: {
      radiant: { label: 'Radiant', desc: 'System thriving' },
      bright: { label: 'Steady ascent', desc: 'Building momentum' },
      emerging: { label: 'Emerging', desc: 'Finding balance' },
      seeking: { label: 'Reorganizing', desc: 'System under strain' },
      awakening: { label: 'Awakening', desc: 'Time for renewal' },
    },
    tr: {
      radiant: { label: 'Parlak', desc: 'Sistem güçlü' },
      bright: { label: 'Yükselişte', desc: 'İvme kazanıyor' },
      emerging: { label: 'Gelişiyor', desc: 'Denge aranıyor' },
      seeking: { label: 'Yeniden yapılanma', desc: 'Sistem zorlanıyor' },
      awakening: { label: 'Uyanış', desc: 'Yenilenme zamanı' },
    }
  };
  
  const lang = interpretations[language] || interpretations.en;
  if (score >= 85) return lang.radiant;
  if (score >= 70) return lang.bright;
  if (score >= 55) return lang.emerging;
  if (score >= 40) return lang.seeking;
  return lang.awakening;
};

const MissionControl = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [solarAlignment, setSolarAlignment] = useState(null);
  const [selectedView, setSelectedView] = useState('current');
  const [hoveredDomain, setHoveredDomain] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [resistance, setResistance] = useState(null);
  const [resistanceLoading, setResistanceLoading] = useState(false);
  const [showShadowPanel, setShowShadowPanel] = useState(false);

  const content = {
    en: {
      title: 'Mission Control',
      solarAlignment: 'Solar Alignment',
      thisMonth: 'this month',
      viewMap: 'View Full Map',
      noDomain: 'Select a domain',
      loading: 'Calibrating systems...',
      noData: 'Complete your first assessment to activate Mission Control',
      startAssessment: 'Begin Assessment',
      mainDomains: 'Main Domains',
      score: 'Score',
      trend: 'Trend',
      improving: 'improving',
      declining: 'declining',
      stable: 'stable',
      shadow: 'Shadow Detection',
      resistanceAppearing: 'Resistance appearing in...',
      loadingShadow: 'Scanning patterns...',
      viewShadow: 'View Shadow Analysis',
      hideShadow: 'Hide',
      inquiry: 'Question for reflection',
    },
    tr: {
      title: 'Komuta Merkezi',
      solarAlignment: 'Güneş Uyumu',
      thisMonth: 'bu ay',
      viewMap: 'Haritayı Görüntüle',
      noDomain: 'Bir alan seçin',
      loading: 'Sistemler kalibre ediliyor...',
      noData: 'Komuta Merkezini aktifleştirmek için ilk değerlendirmenizi tamamlayın',
      startAssessment: 'Değerlendirmeye Başla',
      mainDomains: 'Ana Alanlar',
      score: 'Puan',
      trend: 'Trend',
      improving: 'yükseliyor',
      declining: 'düşüyor',
      stable: 'sabit',
      shadow: 'Gölge Tespiti',
      resistanceAppearing: 'Direnç şurada beliriyor...',
      loadingShadow: 'Kalıplar taranıyor...',
      viewShadow: 'Gölge Analizini Gör',
      hideShadow: 'Gizle',
      inquiry: 'Düşünme sorusu',
    }
  };
  const c = content[language] || content.en;

  // Fetch resistance/shadow data
  const fetchResistance = async () => {
    setResistanceLoading(true);
    try {
      const response = await api.get('/assessment/resistance-detection');
      setResistance(response.data);
    } catch (error) {
      console.error('Failed to fetch resistance data:', error);
    } finally {
      setResistanceLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assessmentRes, solarRes, historyRes] = await Promise.all([
        api.get('/assessment/current'),
        api.get('/assessment/solar-alignment').catch(() => null),
        api.get('/assessment/history').catch(() => ({ data: { assessments: [] } }))
      ]);

      // Parse assessment data - it's nested under 'assessment' key
      const assessmentData = assessmentRes.data.assessment || assessmentRes.data;
      setData(assessmentData);
      
      // Parse solar alignment data
      if (solarRes?.data?.current?.solar_alignment) {
        const solar = solarRes.data.current.solar_alignment;
        setSolarAlignment({
          solar_alignment_score: solar.score,
          interpretation: solar.interpretation,
          trend: solarRes.data.trajectory?.trend || 0
        });
      }
      
      // Get previous assessment for comparison
      const history = historyRes?.data?.assessments || [];
      if (history.length > 1) {
        setPreviousData(history[1]);
      }
    } catch (error) {
      console.error('Failed to load mission control data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare radar chart data
  const radarData = useMemo(() => {
    if (!data?.scores) return [];
    
    const domainOrder = [
      'physical_health', 'strength_fitness', 'energy',
      'work_mission', 'money_financial', 'meaning_purpose',
      'love_relationship', 'friends_community', 'learning_growth',
      'emotional_stability', 'discipline_consistency', 'environment_order'
    ];

    return domainOrder.map(key => {
      const currentScore = data.scores[key] || 0;
      const previousScore = previousData?.scores?.[key] || currentScore;
      const labels = DOMAIN_LABELS[language] || DOMAIN_LABELS.en;
      
      return {
        domain: labels[key] || key,
        key,
        current: currentScore,
        previous: previousScore,
        fullMark: 10,
        isWeak: currentScore < 5,
        isImproving: currentScore > previousScore,
      };
    });
  }, [data, previousData, language]);

  // Calculate main domain averages
  const domainAverages = useMemo(() => {
    if (!data?.scores) return {};
    
    const averages = {};
    Object.entries(DOMAIN_GROUPS).forEach(([groupKey, group]) => {
      const scores = group.subdomains.map(d => data.scores[d] || 0);
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      
      const prevScores = previousData?.scores 
        ? group.subdomains.map(d => previousData.scores[d] || 0)
        : scores;
      const prevAvg = prevScores.reduce((a, b) => a + b, 0) / prevScores.length;
      
      averages[groupKey] = {
        current: avg,
        previous: prevAvg,
        trend: avg > prevAvg ? 'up' : avg < prevAvg ? 'down' : 'stable'
      };
    });
    return averages;
  }, [data, previousData]);

  // Custom radar chart tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#121214] border border-white/10 rounded-lg p-3 shadow-xl">
          <p className="text-[#FAFAFA] font-medium font-outfit">{data.domain}</p>
          <p className="text-[#D4AF37] text-lg font-semibold">{data.current}/10</p>
          {data.previous !== data.current && (
            <p className={`text-xs ${data.isImproving ? 'text-green-400' : 'text-red-400'}`}>
              {data.isImproving ? '↑' : '↓'} from {data.previous}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-[#A1A1AA] font-outfit">{c.loading}</p>
        </motion.div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-6" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <Compass className="w-16 h-16 text-[#D4AF37]/30 mx-auto mb-6" />
          <p className="text-[#A1A1AA] font-outfit mb-8">{c.noData}</p>
          <button
            onClick={() => navigate('/assessment')}
            className="bg-[#D4AF37] text-[#0A0A0B] px-8 py-3 rounded-full font-outfit font-medium hover:bg-[#FFBF00] transition-colors"
          >
            {c.startAssessment}
          </button>
        </motion.div>
        <BottomNav />
      </div>
    );
  }

  const interpretation = getSolarInterpretation(solarAlignment?.solar_alignment_score || 0, language);

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative overflow-hidden pb-24 lg:pb-8">
      {/* Subtle celestial background */}
      <div 
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/bfdf36fb-82fe-46f5-bf48-e9427f3926bc/images/5fcff6e859d8abb2344a21603f90c17673ac0b9ebd8d6c60d758ea1f0be05b70.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 lg:mb-12"
        >
          <h1 
            className="text-2xl lg:text-3xl text-[#FAFAFA] tracking-tight font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {c.title}
          </h1>
        </motion.div>

        {/* Main Grid - 3 columns on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          
          {/* LEFT COLUMN - Main Domains */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-3 space-y-4"
          >
            <p className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] font-outfit font-medium mb-4">
              {c.mainDomains}
            </p>
            
            {Object.entries(DOMAIN_GROUPS).map(([key, group], index) => {
              const Icon = group.icon;
              const avg = domainAverages[key];
              const groupName = group.name[language] || group.name.en;
              
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  className={`
                    bg-[#121214] border rounded-2xl p-4 cursor-pointer transition-all duration-300
                    ${hoveredDomain === key 
                      ? 'border-[#D4AF37]/50 shadow-[0_0_20px_rgba(212,175,55,0.1)]' 
                      : 'border-white/5 hover:border-white/10'
                    }
                  `}
                  onMouseEnter={() => setHoveredDomain(key)}
                  onMouseLeave={() => setHoveredDomain(null)}
                  data-testid={`domain-card-${key}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${group.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: group.color }} />
                      </div>
                      <div>
                        <p className="text-[#FAFAFA] font-outfit font-medium text-sm">{groupName}</p>
                        <p className="text-[#A1A1AA] text-xs font-outfit">
                          {avg?.current?.toFixed(1)}/10
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {avg?.trend === 'up' && <TrendingUp className="w-4 h-4 text-green-400" />}
                      {avg?.trend === 'down' && <TrendingDown className="w-4 h-4 text-red-400" />}
                      {avg?.trend === 'stable' && <Minus className="w-4 h-4 text-[#A1A1AA]" />}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* CENTER COLUMN - Solar Alignment + Sun Map */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-6 space-y-6"
          >
            {/* Solar Alignment Score */}
            <div className="bg-[#121214] border border-white/5 rounded-2xl p-6 lg:p-8 text-center relative overflow-hidden">
              {/* Subtle glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent opacity-50" />
              
              <p className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] font-outfit font-medium mb-4 relative z-10">
                {c.solarAlignment}
              </p>
              
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, type: 'spring' }}
                className="relative z-10"
              >
                <p 
                  className="text-7xl lg:text-8xl text-[#FFFFF0] tracking-tighter"
                  style={{ 
                    fontFamily: 'Cormorant Garamond, serif',
                    textShadow: '0 0 40px rgba(212,175,55,0.2)'
                  }}
                  data-testid="solar-alignment-score"
                >
                  {Math.round(solarAlignment?.solar_alignment_score || 0)}
                </p>
                <p className="text-[#D4AF37] font-outfit font-medium mt-2">
                  "{interpretation.label}"
                </p>
                <p className="text-[#A1A1AA] text-sm font-outfit mt-1">
                  {interpretation.desc}
                </p>
                
                {/* Trend indicator */}
                {solarAlignment?.trend !== undefined && solarAlignment.trend !== 0 && (
                  <div className={`inline-flex items-center gap-1 mt-4 px-3 py-1 rounded-full text-xs font-outfit ${
                    solarAlignment.trend > 0 
                      ? 'bg-green-500/10 text-green-400' 
                      : 'bg-red-500/10 text-red-400'
                  }`}>
                    {solarAlignment.trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {solarAlignment.trend > 0 ? '+' : ''}{solarAlignment.trend} {c.thisMonth}
                  </div>
                )}
              </motion.div>
            </div>

            {/* View Toggle */}
            <div className="flex justify-center gap-2">
              {VIEW_OPTIONS.map(option => (
                <button
                  key={option.id}
                  onClick={() => setSelectedView(option.id)}
                  className={`
                    px-4 py-2 rounded-full text-xs tracking-wider uppercase font-outfit font-medium transition-all duration-300
                    ${selectedView === option.id 
                      ? 'bg-[#D4AF37] text-[#0A0A0B]' 
                      : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-[#FAFAFA]'
                    }
                  `}
                  data-testid={`view-toggle-${option.id}`}
                >
                  {option.label[language] || option.label.en}
                </button>
              ))}
            </div>

            {/* Sun Map Radar Chart */}
            <div 
              className="bg-[#121214] border border-white/5 rounded-2xl p-4 lg:p-6 relative"
              data-testid="radar-chart-container"
            >
              <div className="h-[300px] lg:h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="80%">
                    <PolarGrid 
                      stroke="rgba(255,255,255,0.1)" 
                      strokeWidth={0.5}
                    />
                    <PolarAngleAxis 
                      dataKey="domain" 
                      tick={{ 
                        fill: '#A1A1AA', 
                        fontSize: 10,
                        fontFamily: 'Outfit, sans-serif'
                      }}
                      tickLine={false}
                    />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, 10]} 
                      tick={false}
                      axisLine={false}
                    />
                    
                    {/* Previous month overlay (if viewing comparison) */}
                    {selectedView === 'previous' && (
                      <Radar
                        name="Previous"
                        dataKey="previous"
                        stroke="#A1A1AA"
                        strokeWidth={1}
                        fill="#A1A1AA"
                        fillOpacity={0.1}
                        strokeDasharray="4 4"
                      />
                    )}
                    
                    {/* Current scores */}
                    <Radar
                      name="Current"
                      dataKey="current"
                      stroke="#D4AF37"
                      strokeWidth={2}
                      fill="#D4AF37"
                      fillOpacity={0.2}
                      style={{
                        filter: 'drop-shadow(0 0 15px rgba(212,175,55,0.3))'
                      }}
                    />
                    
                    <Tooltip content={<CustomTooltip />} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend */}
              <div className="flex justify-center gap-6 mt-4 text-xs font-outfit">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#D4AF37]" />
                  <span className="text-[#A1A1AA]">
                    {VIEW_OPTIONS.find(v => v.id === selectedView)?.label[language]}
                  </span>
                </div>
                {selectedView === 'previous' && (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#A1A1AA] opacity-50" />
                    <span className="text-[#A1A1AA]">{content[language]?.previous || 'Previous'}</span>
                  </div>
                )}
              </div>
            </div>

            {/* View Full Dashboard Link */}
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full flex items-center justify-center gap-2 py-3 text-[#D4AF37] hover:text-[#FFBF00] transition-colors font-outfit text-sm"
              data-testid="view-full-map-btn"
            >
              {c.viewMap}
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>

          {/* RIGHT COLUMN - Domain Details on Hover */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-3 space-y-4"
          >
            <AnimatePresence mode="wait">
              {hoveredDomain && DOMAIN_GROUPS[hoveredDomain] ? (
                <motion.div
                  key={hoveredDomain}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-[#121214] border border-[#D4AF37]/30 rounded-2xl p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    {React.createElement(DOMAIN_GROUPS[hoveredDomain].icon, {
                      className: 'w-6 h-6',
                      style: { color: DOMAIN_GROUPS[hoveredDomain].color }
                    })}
                    <h3 className="text-[#FAFAFA] font-outfit font-medium">
                      {DOMAIN_GROUPS[hoveredDomain].name[language]}
                    </h3>
                  </div>
                  
                  <div className="space-y-3">
                    {DOMAIN_GROUPS[hoveredDomain].subdomains.map(subdomain => {
                      const score = data?.scores?.[subdomain] || 0;
                      const prevScore = previousData?.scores?.[subdomain] || score;
                      const labels = DOMAIN_LABELS[language] || DOMAIN_LABELS.en;
                      
                      return (
                        <div key={subdomain} className="flex items-center justify-between">
                          <span className="text-[#A1A1AA] text-sm font-outfit">
                            {labels[subdomain]}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-[#FAFAFA] font-outfit font-medium">
                              {score}
                            </span>
                            {score !== prevScore && (
                              <span className={`text-xs ${score > prevScore ? 'text-green-400' : 'text-red-400'}`}>
                                {score > prevScore ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-[#121214]/50 border border-white/5 rounded-2xl p-6 text-center"
                >
                  <Compass className="w-8 h-8 text-[#D4AF37]/30 mx-auto mb-3" />
                  <p className="text-[#A1A1AA] text-sm font-outfit">{c.noDomain}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Quick Stats */}
            <div className="bg-[#121214] border border-white/5 rounded-2xl p-6">
              <p className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] font-outfit font-medium mb-4">
                {c.trend}
              </p>
              <div className="space-y-3">
                {radarData.filter(d => d.isWeak || d.isImproving).slice(0, 4).map(item => (
                  <div key={item.key} className="flex items-center justify-between">
                    <span className="text-[#FAFAFA] text-sm font-outfit">{item.domain}</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-outfit ${
                      item.isWeak 
                        ? 'bg-amber-500/10 text-amber-400' 
                        : 'bg-green-500/10 text-green-400'
                    }`}>
                      {item.isWeak ? (language === 'tr' ? 'Odak' : 'Focus') : (language === 'tr' ? '↑' : '↑')}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Shadow / Resistance Detection Panel */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-[#121214] border border-white/5 rounded-2xl overflow-hidden"
            >
              {/* Header - Always visible */}
              <button
                onClick={() => {
                  if (!resistance && !resistanceLoading) {
                    fetchResistance();
                  }
                  setShowShadowPanel(!showShadowPanel);
                }}
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                data-testid="shadow-panel-toggle"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Eye className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-[#FAFAFA] text-sm font-outfit font-medium">{c.shadow}</p>
                    <p className="text-[#A1A1AA] text-xs font-outfit">
                      {resistance ? c.resistanceAppearing : c.viewShadow}
                    </p>
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 text-[#A1A1AA] transition-transform ${showShadowPanel ? 'rotate-90' : ''}`} />
              </button>

              {/* Expanded Shadow Content */}
              <AnimatePresence>
                {showShadowPanel && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 border-t border-white/5">
                      {resistanceLoading ? (
                        <div className="py-8 text-center">
                          <Loader2 className="w-6 h-6 text-purple-400 animate-spin mx-auto mb-2" />
                          <p className="text-[#A1A1AA] text-xs font-outfit">{c.loadingShadow}</p>
                        </div>
                      ) : resistance ? (
                        <div className="pt-4 space-y-4">
                          {/* Resistance Patterns */}
                          {resistance.patterns?.length > 0 && (
                            <div className="space-y-2">
                              {resistance.patterns.map((pattern, idx) => {
                                const patternConfig = RESISTANCE_PATTERNS[pattern.pattern_type] || {
                                  icon: AlertTriangle,
                                  color: '#A1A1AA',
                                  label: { en: pattern.pattern_type, tr: pattern.pattern_type }
                                };
                                const PatternIcon = patternConfig.icon;
                                
                                return (
                                  <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="bg-white/5 rounded-xl p-3"
                                  >
                                    <div className="flex items-start gap-3">
                                      <div 
                                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                        style={{ backgroundColor: `${patternConfig.color}15` }}
                                      >
                                        <PatternIcon 
                                          className="w-4 h-4" 
                                          style={{ color: patternConfig.color }} 
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-[#FAFAFA] text-sm font-outfit font-medium">
                                            {patternConfig.label[language] || patternConfig.label.en}
                                          </span>
                                          <span className={`text-xs px-2 py-0.5 rounded-full font-outfit ${
                                            pattern.intensity === 'high' ? 'bg-red-500/20 text-red-400' :
                                            pattern.intensity === 'moderate' ? 'bg-amber-500/20 text-amber-400' :
                                            'bg-green-500/20 text-green-400'
                                          }`}>
                                            {pattern.intensity}
                                          </span>
                                        </div>
                                        <p className="text-[#A1A1AA] text-xs font-outfit leading-relaxed">
                                          {pattern.description}
                                        </p>
                                      </div>
                                    </div>
                                  </motion.div>
                                );
                              })}
                            </div>
                          )}

                          {/* Core Insight */}
                          {resistance.core_insight && (
                            <div className="pt-3 border-t border-white/5">
                              <p className="text-[#FFFFF0] text-sm font-outfit leading-relaxed italic">
                                "{resistance.core_insight}"
                              </p>
                            </div>
                          )}

                          {/* Shadow Message */}
                          {resistance.shadow_message && (
                            <div className="bg-purple-500/10 rounded-xl p-4">
                              <p className="text-purple-300 text-sm font-outfit leading-relaxed">
                                {resistance.shadow_message}
                              </p>
                            </div>
                          )}

                          {/* Reflection Question */}
                          {resistance.recommended_inquiry && (
                            <div className="pt-2">
                              <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-outfit mb-2">
                                {c.inquiry}
                              </p>
                              <p className="text-[#D4AF37] text-sm font-outfit">
                                "{resistance.recommended_inquiry}"
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="py-6 text-center">
                          <p className="text-[#A1A1AA] text-sm font-outfit">
                            {language === 'tr' 
                              ? 'Gölge analizini başlatmak için tıklayın' 
                              : 'Click to reveal shadow analysis'}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default MissionControl;
